<template>
  <div class="form-section">
    <h2 class="section-title">1. 이벤트 기본 정보</h2>
    <p class="section-description">이벤트의 기본적인 정보를 설정하세요. 이벤트 유형과 진행 방식에 따라 필요한 기능이 자동으로 추천됩니다.</p>
    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item
          label="이벤트 주최자 (복수 선택 가능)"
          name="organizers"
          required
        >
          <!-- 선택된 주최자 목록 -->
          <div v-if="form.organizers && form.organizers.length > 0" class="organizers-list">
            <a-tag
              v-for="(organizer, index) in form.organizers"
              :key="`org-${index}`"
              closable
              @close="removeOrganizer(index)"
              class="organizer-tag"
            >
              <span :class="['organizer-type-badge', organizer.type === 'MANUFACTURER' ? 'manufacturer' : 'hospital']">
                {{ organizer.type === 'MANUFACTURER' ? '제조사' : '병원' }}
              </span>
              <span class="organizer-name">{{ organizer.organizerName }}</span>
              <span v-if="organizer.representative || organizer.organizerCno" class="sub-info">
                ({{ [organizer.representative, organizer.organizerCno].filter(Boolean).join(' / ') }})
              </span>
            </a-tag>
          </div>
          
          <!-- 입력 영역 -->
          <div class="organizer-input-area">
            <a-radio-group v-model:value="inputMode" class="input-mode-selector">
              <a-radio-button value="search">주최자 검색</a-radio-button>
              <a-radio-button value="direct">직접 입력</a-radio-button>
            </a-radio-group>
            
            <!-- 검색 모드 -->
            <div v-if="inputMode === 'search'" class="search-mode">
              <a-button type="primary" class="yellow-button" @click="handleSearchModalOpen">
                <i class="fa-solid fa-search"></i> 주최자 검색
              </a-button>
            </div>
            
            <!-- 직접 입력 모드 -->
            <div v-else class="direct-mode">
              <div style="display: flex; gap: 8px;">
                <input 
                  v-model="directInputValue"
                  type="text"
                  placeholder="주최자명 입력"
                  class="custom-input"
                  @keyup.enter="addDirectOrganizer"
                />
                <a-button type="primary" @click="addDirectOrganizer">
                  추가
                </a-button>
              </div>
            </div>
          </div>
        </a-form-item>
      </form-item>
    </div>
    
    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item
          label="이벤트 제목"
          name="eventTitle"
          required
          tooltip="최종 사용자가 보게 될 이벤트의 제목입니다"
        >
          <a-input v-model:value="form.eventTitle" placeholder="예: 시술후 인증서 발급 서비스 웜업 테스트" />
        </a-form-item>
      </form-item>
    </div>

    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item 
          label="이벤트 시나리오 유형" 
          name="scenarioType"
          tooltip="이벤트의 시나리오 유형을 선택하세요"
        >
          <div v-if="!isCustomScenarioType" class="event-type-selector">
            <a-radio-group v-model:value="form.scenarioType" @change="handleScenarioTypeChange">
              <a-radio-button value="review">리뷰 이벤트</a-radio-button>
              <a-radio-button value="share">공유 이벤트</a-radio-button>
              <a-radio-button value="custom">직접 입력</a-radio-button>
            </a-radio-group>
          </div>
          
          <div v-else class="custom-event-type">
            <div style="display: flex; gap: 8px; align-items: center;">
              <a-input 
                v-model:value="form.customScenarioType" 
                placeholder="시나리오 유형을 입력해주세요 (예: 체험단 이벤트)"
                style="flex: 1; max-width: 400px;"
              />
              <a-button @click="cancelCustomScenarioType">
                취소
              </a-button>
            </div>
            <div class="description-text" style="margin-top: 8px;">
              직접 입력한 시나리오 유형이 저장됩니다.
            </div>
          </div>
        </a-form-item>
      </form-item>
    </div>

    <div class="form-row">
      <form-item>
        <a-form-item
          label="시작일"
          name="startDate"
          required
          tooltip="이벤트가 시작되는 날짜입니다. 이 날짜부터 사용자가 참여할 수 있습니다."
        >
          <a-date-picker 
            v-model:value="form.startDate" 
            style="width: 100%" 
            format="YYYY-MM-DD"
            :disabled-date="disabledStartDate"
            placeholder="시작일 선택"
          />
        </a-form-item>
      </form-item>
      
      <form-item>
        <a-form-item
          label="종료일"
          name="endDate"
          required
          tooltip="이벤트가 종료되는 날짜입니다. 이 날짜 이후에는 사용자 참여가 제한됩니다."
        >
          <a-date-picker 
            v-model:value="form.endDate" 
            style="width: 100%" 
            format="YYYY-MM-DD"
            :disabled-date="disabledEndDate"
            placeholder="종료일 선택"
          />
        </a-form-item>
      </form-item>
    </div>

    <div class="form-row">
      <form-item>
        <a-form-item
          label="이벤트 상태"
          name="eventStatus"
          tooltip="이벤트의 현재 상태를 설정합니다"
        >
          <a-select v-model:value="form.eventStatus" :disabled="!isEdit">
            <a-select-option value="preparing">준비중</a-select-option>
            <a-select-option value="ongoing">진행중</a-select-option>
            <a-select-option value="completed">종료됨</a-select-option>
          </a-select>
          <div class="description-text">
            <span v-if="!isEdit">이벤트 생성 시에는 '준비중' 상태로 생성됩니다. 생성 후 수정에서 상태를 변경할 수 있습니다.</span>
            <span v-else-if="form.eventStatus === 'preparing'">준비 중인 이벤트는 사용자에게 보이지 않습니다.</span>
            <span v-else-if="form.eventStatus === 'ongoing'">진행 중인 이벤트는 모든 사용자가 참여할 수 있습니다.</span>
            <span v-else>종료된 이벤트는 더 이상 참여할 수 없지만 결과는 확인할 수 있습니다.</span>
          </div>
        </a-form-item>
      </form-item>
    </div>

    <!-- 인증서 수신 페이지 모드 (REQ-R3-C1: BasicInfoSection에 배치) -->
    <div class="form-row">
      <form-item>
        <a-form-item
          label="수신 페이지 모드"
          name="certificateReceivePageType"
          tooltip="카카오톡 알림톡 URL로 접속한 고객에게 표시할 인증서 수신 페이지 형식을 선택합니다"
        >
          <a-radio-group v-model:value="form.certificateReceivePageType">
            <a-radio value="event">
              이벤트형
              <span class="radio-hint">응모 버튼 노출, 카카오 링크에 eventId 포함</span>
            </a-radio>
            <a-radio value="normal">
              일반형
              <span class="radio-hint">내 인증서 보러가기 버튼만 노출, 참여자 자동 등록은 유지</span>
            </a-radio>
          </a-radio-group>
          <!-- AC5-S4: ongoing 상태일 때만 즉시 반영 안내 표시 -->
          <div v-if="form.eventStatus === 'ongoing'" class="description-text description-text--warning">
            현재 이 설정이 알림톡 URL로 발송된 모든 수신 페이지에 즉시 반영됩니다.
          </div>
          <div v-else class="description-text">
            현재 수신 페이지에 영향 없음 (이벤트 진행 중일 때만 적용됨)
          </div>
        </a-form-item>
      </form-item>
    </div>

    <!-- 쿠폰함 페이지 이벤트 유도 팝업 표시 여부 (REQ-R2-C1: 수신 페이지 모드 라디오 바로 아래 배치) -->
    <div class="form-row">
      <form-item>
        <a-form-item
          label="쿠폰함 팝업 표시"
          name="couponPageEventModalEnabled"
          tooltip="쿠폰함 페이지에서 이벤트 참여 유도 팝업(더 큰 경품 도전하기) 표시 여부를 설정합니다"
        >
          <!-- REQ-R2-C1: a-checkbox, REQ-R2-C2: 라벨 텍스트 -->
          <a-checkbox v-model:checked="form.couponPageEventModalEnabled">
            쿠폰함 페이지에서 이벤트 참여 유도 팝업 표시
          </a-checkbox>
          <!-- REQ-R2-C3: 힌트 텍스트 -->
          <div class="radio-hint" style="margin-top: 4px; margin-left: 0;">
            끄면 쿠폰함 페이지 진입, 상세 닫기, 쿠폰 받기 이후 '더 큰 경품 도전하기' 모달이 모두 표시되지 않습니다
          </div>
        </a-form-item>
      </form-item>
    </div>

    <!-- 후기 화면: 후기 요청 알림톡 링크(/event/share/review)를 연 고객에게 보여 줄 화면 -->
    <div class="form-row">
      <form-item>
        <a-form-item
          label="후기 화면"
          name="reviewPageType"
          tooltip="후기 요청 알림톡의 후기 작성 링크를 연 고객에게 보여 줄 화면을 선택합니다"
        >
          <a-radio-group v-model:value="form.reviewPageType">
            <a-radio value="default">
              기본형
              <span class="radio-hint">기존 후기 화면. 후기 작성 뒤 당첨 쿠폰 안내가 나옵니다</span>
            </a-radio>
            <a-radio value="zeiss-v1">
              자이스 v1
              <span class="radio-hint">자이스 스마일 후기 화면. 쿠폰 안내 없이 완료 화면에 「병원 보러가기」만 나옵니다</span>
            </a-radio>
          </a-radio-group>
          <div class="description-text">
            화면만 바뀝니다. 커피 쿠폰 같은 후기 보상 지급 여부는 아래 「보상 임계치 설정」에서 정합니다.
          </div>
          <div v-if="form.eventStatus === 'ongoing'" class="description-text description-text--warning">
            저장하면 이 이벤트의 후기 링크에 바로 반영됩니다.
          </div>
        </a-form-item>
      </form-item>
    </div>

    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item
          label="이벤트 설명"
          name="description"
          tooltip="이벤트의 상세 설명을 입력하세요. 이 내용은 관리자 페이지에서만 보입니다."
        >
          <a-textarea v-model:value="form.description" :rows="4" placeholder="이벤트에 대한 상세 설명을 입력하세요." />
        </a-form-item>
      </form-item>
    </div>

    <!-- 알림톡 템플릿 코드 설정 -->
    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item
          label="알림톡 템플릿 코드"
          name="alimtalkTemplateCode"
          tooltip="이벤트에 사용할 알림톡 템플릿 코드를 입력하세요. 미입력 시 시스템 기본 템플릿이 사용됩니다."
        >
          <div class="alimtalk-template-input">
            <a-input
              v-model:value="form.alimtalkTemplateCode"
              placeholder="예: DEV_ZEISS_EVENT_202509_V3"
              style="max-width: 400px;"
            />
            <a-button
              v-if="form.alimtalkTemplateCode"
              type="link"
              @click="previewTemplate"
              :loading="templateLoading"
            >
              <i class="fa-solid fa-eye"></i> 미리보기
            </a-button>
          </div>
          <div class="description-text">
            미입력 시 환경설정의 ZEISS_EVENT 값이 사용됩니다.
          </div>
        </a-form-item>
      </form-item>
    </div>

    <!-- 환경변수 설정 키 -->
    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item
          label="환경변수 설정 키"
          name="envConfigKey"
          tooltip="이벤트별 고유 환경변수 키입니다. 이벤트 상태가 '진행중'으로 변경되면 이 키에 이벤트 ID가 자동 설정됩니다."
        >
          <div class="env-config-key-input">
            <a-input
              v-model:value="form.envConfigKey"
              placeholder="예: ZEISS_EVENT_2025_FALL"
              style="max-width: 400px;"
            />
          </div>
          <div class="description-text">
            인증서 발행 시 이 키로 활성 이벤트 여부를 판단합니다. ZEISS 이벤트는 'ZEISS_EVENT_'로 시작해야 합니다.
          </div>
        </a-form-item>
      </form-item>
    </div>

    <!-- 이벤트 적용 범위 설정 -->
    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item
          label="이벤트 적용 범위"
          name="targetScope"
          tooltip="이벤트가 적용될 병원 범위를 설정합니다. '모든 병원'을 선택하면 모든 병원에서 발행되는 인증서에 이벤트가 적용되고, '특정 병원만'을 선택하면 지정한 병원에서만, '특정 병원 제외'를 선택하면 지정한 병원을 제외한 나머지 병원에 이벤트가 적용됩니다."
        >
          <a-radio-group v-model:value="form.targetScope" @change="handleTargetScopeChange">
            <a-radio-button value="ALL">모든 병원</a-radio-button>
            <a-radio-button value="SPECIFIC">특정 병원만</a-radio-button>
            <a-radio-button value="EXCLUDE">특정 병원 제외</a-radio-button>
          </a-radio-group>
          <div class="description-text">
            <span v-if="form.targetScope === 'ALL'">
              이벤트 상태가 '진행중'일 때, 모든 병원에서 발행되는 인증서에 이벤트가 자동 적용됩니다.
            </span>
            <span v-else-if="form.targetScope === 'SPECIFIC'">
              이벤트 상태가 '진행중'일 때, 아래 지정된 병원에서 발행되는 인증서에만 이벤트가 적용됩니다.
            </span>
            <span v-else-if="form.targetScope === 'EXCLUDE'">
              이벤트 상태가 '진행중'일 때, 아래 지정된 병원을 <strong>제외한</strong> 모든 병원에서 발행되는 인증서에 이벤트가 적용됩니다.
            </span>
          </div>
        </a-form-item>
      </form-item>
    </div>

    <!-- 특정 병원 지정 (targetScope가 SPECIFIC 또는 EXCLUDE일 때 표시) - 적용 범위 바로 아래 배치 -->
    <div v-if="form.targetScope === 'SPECIFIC' || form.targetScope === 'EXCLUDE'" class="form-row">
      <form-item :fullWidth="true">
        <a-form-item
          :label="form.targetScope === 'EXCLUDE' ? '제외할 병원' : '적용 대상 병원'"
          name="targetHospitals"
          :rules="[{ required: form.targetScope === 'SPECIFIC' || form.targetScope === 'EXCLUDE', message: form.targetScope === 'EXCLUDE' ? '제외할 병원을 1개 이상 선택해주세요' : '적용 대상 병원을 1개 이상 선택해주세요' }]"
        >
          <!-- 선택된 병원 목록 -->
          <div v-if="form.targetHospitals && form.targetHospitals.length > 0" class="target-hospitals-list">
            <a-tag
              v-for="(hospital, index) in form.targetHospitals"
              :key="`hospital-${index}`"
              closable
              @close="removeTargetHospital(index)"
              class="hospital-tag"
            >
              {{ hospital.hospitalName }}
              <span class="hospital-id-hint">({{ hospital.hospitalId.slice(-6) }})</span>
            </a-tag>
          </div>

          <!-- 병원 검색 버튼 -->
          <div class="hospital-input-area">
            <a-button type="primary" class="yellow-button" @click="handleHospitalSearchModalOpen">
              <i class="fa-solid fa-search"></i> 병원 검색
            </a-button>
            <div class="description-text" style="margin-top: 8px;">
              이벤트가 적용될 병원을 검색하여 선택하세요. 주최자 검색과 동일한 데이터를 사용합니다.
            </div>
          </div>
        </a-form-item>
      </form-item>
    </div>

    <!-- 과거 이벤트 참여자 차단 설정 -->
    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item
          label="과거 이벤트 참여자 차단"
          name="blockPreviousParticipants"
        >
          <div class="block-config-container">
            <!-- 기능 활성화 스위치 -->
            <div class="block-config-header">
              <a-switch v-model:checked="form.blockPreviousParticipants.enabled" />
              <span style="margin-left: 12px;">
                {{ form.blockPreviousParticipants.enabled ? '차단 활성화' : '차단 비활성화' }}
              </span>
              <a-popover placement="rightTop" trigger="click" overlayClassName="block-logic-popover">
                <template #content>
                  <div class="block-logic-diagram">
                    <div class="diagram-title">차단 로직 플로우</div>
                    <div class="diagram-flow">
                      <div class="flow-start">참여 요청</div>
                      <div class="flow-arrow">▼</div>
                      <div class="flow-checks">
                        <div class="check-item">
                          <div class="check-box">1. 전역 체크</div>
                          <div class="check-desc">모든 이벤트에서 참여 기록 확인</div>
                        </div>
                        <div class="flow-or">OR</div>
                        <div class="check-item">
                          <div class="check-box">2. 같은 주최자</div>
                          <div class="check-desc">동일 주최자 이벤트 확인</div>
                        </div>
                        <div class="flow-or">OR</div>
                        <div class="check-item">
                          <div class="check-box">3. 특정 주최자</div>
                          <div class="check-desc">선택한 주최자 이벤트 확인</div>
                        </div>
                        <div class="flow-or">OR</div>
                        <div class="check-item">
                          <div class="check-box">4. 특정 이벤트</div>
                          <div class="check-desc">선택한 이벤트에서 확인</div>
                        </div>
                      </div>
                      <div class="flow-arrow">▼</div>
                      <div class="flow-results">
                        <div class="result-block blocked">
                          <div class="result-icon">🚫</div>
                          <div class="result-text">하나라도 해당 → 차단</div>
                        </div>
                        <div class="result-block passed">
                          <div class="result-icon">✅</div>
                          <div class="result-text">모두 통과 → 참여 허용</div>
                        </div>
                      </div>
                    </div>
                    <div class="diagram-note">
                      <warning-outlined style="color: #faad14; margin-right: 4px;" />
                      현재 이벤트는 모든 체크에서 제외됩니다
                    </div>
                  </div>
                </template>
                <question-circle-outlined style="margin-left: 8px; color: #1890ff; cursor: pointer;" />
              </a-popover>
            </div>

            <!-- 차단 범위 설정 (enabled일 때만 표시) -->
            <div v-if="form.blockPreviousParticipants.enabled" class="block-config-options">
              <div class="block-scope-title">차단 범위 선택 (복수 선택 가능 - OR 조건)</div>

              <!-- 전역 체크 -->
              <div class="block-scope-item">
                <a-checkbox v-model:checked="form.blockPreviousParticipants.checkAll">
                  모든 이벤트 (전역)
                </a-checkbox>
                <div class="block-scope-desc">모든 이벤트에서 참여 기록을 체크합니다.</div>
                <div v-if="form.blockPreviousParticipants.checkAll" class="block-scope-message">
                  <label class="message-label">차단 시 사용자에게 표시될 메시지</label>
                  <a-textarea
                    v-model:value="form.blockPreviousParticipants.checkAllMessage"
                    :rows="2"
                    placeholder="차단 시 사용자에게 표시할 메시지를 입력하세요"
                    :maxlength="200"
                    show-count
                  />
                </div>
              </div>

              <!-- 같은 주최자 체크 -->
              <div class="block-scope-item">
                <a-checkbox v-model:checked="form.blockPreviousParticipants.checkSameOrganizer">
                  같은 주최자의 이벤트
                </a-checkbox>
                <div class="block-scope-desc">
                  현재 이벤트와 동일한 주최자(organizer)의 다른 이벤트에서 참여 기록을 체크합니다.
                  <a-tooltip>
                    <template #title>
                      주최자가 여러 명인 경우, 하나라도 일치하면 차단 대상이 됩니다.
                      예: 현재 이벤트 주최자가 [A, B]이고 과거 이벤트 주최자가 [B, C]인 경우 B가 일치하므로 차단됩니다.
                    </template>
                    <question-circle-outlined style="margin-left: 4px; color: #999;" />
                  </a-tooltip>
                </div>
                <div v-if="form.blockPreviousParticipants.checkSameOrganizer" class="block-scope-message">
                  <label class="message-label">차단 시 사용자에게 표시될 메시지</label>
                  <a-textarea
                    v-model:value="form.blockPreviousParticipants.checkSameOrganizerMessage"
                    :rows="2"
                    placeholder="차단 시 사용자에게 표시할 메시지를 입력하세요"
                    :maxlength="200"
                    show-count
                  />
                </div>
              </div>

              <!-- 특정 주최자 체크 -->
              <div class="block-scope-item">
                <a-checkbox v-model:checked="form.blockPreviousParticipants.checkSpecificOrganizers">
                  특정 주최자의 이벤트
                </a-checkbox>
                <div class="block-scope-desc">지정된 주최자들의 이벤트에서 참여 기록을 체크합니다.</div>
                <div v-if="form.blockPreviousParticipants.checkSpecificOrganizers" class="block-scope-input">
                  <a-select
                    v-model:value="form.blockPreviousParticipants.targetOrganizerIds"
                    mode="multiple"
                    placeholder="차단 대상 주최자를 선택하세요"
                    :loading="organizerListLoading"
                    :filter-option="filterOrganizerOption"
                    show-search
                    style="width: 100%"
                    :max-tag-count="2"
                    :max-tag-placeholder="(omittedValues) => `+${omittedValues.length} 더보기`"
                    option-label-prop="label"
                  >
                    <a-select-option
                      v-for="organizer in organizerList"
                      :key="organizer.id"
                      :value="organizer.id"
                      :label="organizer.name"
                    >
                      <div class="organizer-option">
                        <span :class="['organizer-type-tag', organizer.type === 'MANUFACTURER' ? 'manufacturer' : 'hospital']">
                          {{ getOrganizerTypeText(organizer.type) }}
                        </span>
                        <span class="organizer-name">{{ organizer.name }}</span>
                        <span v-if="organizer.representative || organizer.businessNumber" class="organizer-details">
                          ({{ [organizer.representative, organizer.businessNumber].filter(Boolean).join(', ') }})
                        </span>
                      </div>
                    </a-select-option>
                  </a-select>
                  <div class="input-count">{{ form.blockPreviousParticipants.targetOrganizerIds.length }}개 주최자 선택됨</div>
                  <div class="block-scope-message">
                    <label class="message-label">차단 시 사용자에게 표시될 메시지</label>
                    <a-textarea
                      v-model:value="form.blockPreviousParticipants.checkSpecificOrganizersMessage"
                      :rows="2"
                      placeholder="차단 시 사용자에게 표시할 메시지를 입력하세요"
                      :maxlength="200"
                      show-count
                    />
                  </div>
                </div>
              </div>

              <!-- 특정 이벤트 체크 -->
              <div class="block-scope-item">
                <a-checkbox v-model:checked="form.blockPreviousParticipants.checkSpecificEvents">
                  특정 이벤트
                </a-checkbox>
                <div class="block-scope-desc">지정된 특정 이벤트들에서 참여 기록을 체크합니다.</div>
                <div v-if="form.blockPreviousParticipants.checkSpecificEvents" class="block-scope-input">
                  <a-select
                    v-model:value="form.blockPreviousParticipants.targetEventIds"
                    mode="multiple"
                    placeholder="차단 대상 이벤트를 선택하세요"
                    :loading="eventListLoading"
                    :filter-option="filterEventOption"
                    show-search
                    style="width: 100%"
                    :max-tag-count="3"
                    :max-tag-placeholder="(omittedValues) => `+${omittedValues.length} 더보기`"
                    option-label-prop="label"
                  >
                    <a-select-option
                      v-for="event in eventList"
                      :key="event._id"
                      :value="event._id"
                      :label="event.eventTitle"
                    >
                      <div class="event-option">
                        <span :class="['event-status-tag', event.eventStatus]">
                          {{ getEventStatusText(event.eventStatus) }}
                        </span>
                        <span class="event-title">{{ event.eventTitle }}</span>
                        <span v-if="getEventOrganizers(event)" class="event-organizers">
                          ({{ getEventOrganizers(event) }})
                        </span>
                      </div>
                    </a-select-option>
                  </a-select>
                  <div class="input-count">{{ form.blockPreviousParticipants.targetEventIds.length }}개 이벤트 선택됨</div>
                  <div class="block-scope-message">
                    <label class="message-label">차단 시 사용자에게 표시될 메시지</label>
                    <a-textarea
                      v-model:value="form.blockPreviousParticipants.checkSpecificEventsMessage"
                      :rows="2"
                      placeholder="차단 시 사용자에게 표시할 메시지를 입력하세요"
                      :maxlength="200"
                      show-count
                    />
                  </div>
                </div>
              </div>

              <!-- 세그먼트 필터 설정 -->
              <div class="block-segment-filter">
                <div class="block-scope-title" style="margin-top: 16px; border-top: 1px solid #e8e8e8; padding-top: 16px;">
                  차단 대상 세그먼트 필터
                  <a-tooltip>
                    <template #title>
                      차단 대상 참여자를 세그먼트(인증서 보유 여부)로 필터링합니다.
                      예: '인증서 보유자만' 선택 시 인증서 미보유자의 과거 참여 기록은 무시합니다.
                    </template>
                    <question-circle-outlined style="margin-left: 4px; color: #999;" />
                  </a-tooltip>
                </div>
                <a-radio-group v-model:value="form.blockPreviousParticipants.segmentFilter" class="segment-filter-group">
                  <a-radio value="ALL">
                    <span class="segment-option">
                      <span class="segment-label">전체</span>
                      <span class="segment-desc">모든 참여자의 과거 기록 체크 (기본값)</span>
                    </span>
                  </a-radio>
                  <a-radio value="CERTIFICATE_HOLDER">
                    <span class="segment-option">
                      <span class="segment-label">인증서 보유자만</span>
                      <span class="segment-desc">인증서 보유자의 과거 참여만 차단 대상</span>
                    </span>
                  </a-radio>
                  <a-radio value="NON_CERTIFICATE">
                    <span class="segment-option">
                      <span class="segment-label">인증서 미보유자만</span>
                      <span class="segment-desc">인증서 미보유자의 과거 참여만 차단 대상</span>
                    </span>
                  </a-radio>
                </a-radio-group>
              </div>

              <!-- 선택된 범위 요약 -->
              <div v-if="hasAnyBlockScope" class="block-scope-summary">
                <strong>적용 범위:</strong>
                <span v-if="form.blockPreviousParticipants.checkAll" class="scope-tag">전역</span>
                <span v-if="form.blockPreviousParticipants.checkSameOrganizer" class="scope-tag">같은 주최자</span>
                <span v-if="form.blockPreviousParticipants.checkSpecificOrganizers" class="scope-tag">특정 주최자 ({{ form.blockPreviousParticipants.targetOrganizerIds.length }}개)</span>
                <span v-if="form.blockPreviousParticipants.checkSpecificEvents" class="scope-tag">특정 이벤트 ({{ form.blockPreviousParticipants.targetEventIds.length }}개)</span>
                <span v-if="form.blockPreviousParticipants.segmentFilter && form.blockPreviousParticipants.segmentFilter !== 'ALL'" class="scope-tag segment">
                  {{ form.blockPreviousParticipants.segmentFilter === 'CERTIFICATE_HOLDER' ? '인증서 보유자만' : '미보유자만' }}
                </span>
              </div>
              <div v-else class="block-scope-warning">
                <warning-outlined style="color: #faad14;" /> 최소 하나의 범위를 선택해야 합니다.
              </div>
            </div>

            <div v-else class="description-text">
              다른 이벤트 참여 여부와 관계없이 본 이벤트에 참여할 수 있습니다. (기본값)
            </div>
          </div>
        </a-form-item>
      </form-item>
    </div>

    <!-- 쿠폰 재고 소진 설정 -->
    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item
          label="쿠폰 재고 소진"
          name="isCouponDepleted"
          tooltip="활성화 시, 쿠폰 발급이 일시적으로 차단됩니다. 이벤트가 진행 중이어도 쿠폰 발급을 막을 수 있습니다."
        >
          <a-switch v-model:checked="form.isCouponDepleted" />
          <span style="margin-left: 12px;" :style="{ color: form.isCouponDepleted ? '#ff4d4f' : '#52c41a' }">
            {{ form.isCouponDepleted ? '소진됨 (발급 차단)' : '발급 가능' }}
          </span>
          <div class="description-text">
            <span v-if="form.isCouponDepleted" style="color: #ff4d4f;">
              쿠폰 재고가 소진되어 발급이 차단됩니다.
            </span>
            <span v-else>
              쿠폰 발급이 가능합니다. 이벤트 상태가 '종료됨'이면 이 설정과 관계없이 발급이 차단됩니다.
            </span>
          </div>
          <!-- 쿠폰 소진 메시지 입력 -->
          <div v-if="form.isCouponDepleted" class="coupon-depleted-message">
            <label class="message-label">쿠폰 소진 시 사용자에게 표시될 메시지</label>
            <a-textarea
              v-model:value="form.couponDepletedMessage"
              :rows="2"
              placeholder="쿠폰 소진 시 사용자에게 표시할 메시지를 입력하세요"
              :maxlength="200"
              show-count
            />
          </div>
        </a-form-item>
      </form-item>
    </div>

    <!-- 환자/친구별 최대 쿠폰 수량 설정 -->
    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item
          label="환자/친구 최대 쿠폰 수량"
          name="couponLimits"
          tooltip="환자와 친구가 각각 받을 수 있는 최대 쿠폰 수량을 설정합니다. 이 설정은 자동 발급 및 관리자 수동 추가 모두에 적용됩니다."
        >
          <div class="coupon-limits-container">
            <div class="coupon-limit-item">
              <div class="limit-header">
                <span class="limit-badge patient">환자</span>
                <span class="limit-label">최대 쿠폰 수량</span>
              </div>
              <a-input-number
                v-model:value="form.couponLimits.patient.maxCoupons"
                :min="1"
                :max="100"
                style="width: 120px;"
              />
              <span class="limit-unit">장</span>
            </div>
            <div class="coupon-limit-item">
              <div class="limit-header">
                <span class="limit-badge friend">친구</span>
                <span class="limit-label">최대 쿠폰 수량</span>
              </div>
              <a-input-number
                v-model:value="form.couponLimits.friend.maxCoupons"
                :min="1"
                :max="100"
                style="width: 120px;"
              />
              <span class="limit-unit">장</span>
            </div>
          </div>
          <div class="description-text">
            환자는 본인 인증서로 직접 참여한 사용자, 친구는 공유 링크를 통해 참여한 사용자입니다.
            설정한 수량 이상의 쿠폰을 받을 수 없습니다.
          </div>
        </a-form-item>
      </form-item>
    </div>

    <!-- 고객사 레포트 노출 설정 -->
    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item
          label="고객사 레포트 노출"
          name="isReportVisible"
          tooltip="활성화 시, 고객사 레포트 페이지의 이벤트 필터에 이 이벤트가 노출됩니다."
        >
          <a-switch v-model:checked="form.isReportVisible" />
          <span style="margin-left: 12px;" :style="{ color: form.isReportVisible ? '#52c41a' : '#8c8c8c' }">
            {{ form.isReportVisible ? '레포트에 노출' : '레포트에 미노출' }}
          </span>
          <div class="description-text">
            <span v-if="form.isReportVisible" style="color: #52c41a;">
              고객사 레포트 페이지에서 이 이벤트를 선택할 수 있습니다.
            </span>
            <span v-else>
              고객사 레포트 페이지의 이벤트 필터에 이 이벤트가 표시되지 않습니다.
            </span>
          </div>
        </a-form-item>
      </form-item>
    </div>

    <!-- 템플릿 미리보기 모달 -->
    <a-modal
      v-model:visible="templatePreviewVisible"
      title="알림톡 템플릿 미리보기"
      :footer="null"
      width="600px"
    >
      <div v-if="templatePreviewData" class="template-preview">
        <div class="preview-item">
          <label>템플릿 코드:</label>
          <span>{{ templatePreviewData.code }}</span>
        </div>
        <div class="preview-item">
          <label>템플릿명:</label>
          <span>{{ templatePreviewData.name }}</span>
        </div>
        <div class="preview-item">
          <label>메시지 내용:</label>
          <pre class="message-content">{{ templatePreviewData.content }}</pre>
        </div>
        <div v-if="templatePreviewData.buttons && templatePreviewData.buttons.length" class="preview-item">
          <label>버튼:</label>
          <div class="buttons-list">
            <div v-for="(btn, idx) in templatePreviewData.buttons" :key="idx" class="button-item">
              {{ btn.name }} ({{ btn.type }})
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="templateLoading" class="template-loading">
        <a-spin /> 템플릿 정보를 불러오는 중...
      </div>
      <div v-else class="template-error">
        템플릿 정보를 불러올 수 없습니다.
      </div>
    </a-modal>

    <!-- 보상 임계치 설정 -->
    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item label="보상 임계치 설정">
          <div class="reward-threshold-header">
            <span>이벤트 참여 조건에 따른 보상을 설정합니다</span>
            <a-button type="primary" class="yellow-button" @click="addRewardThreshold">
              <i class="fa-solid fa-plus"></i> 보상 조건 추가
            </a-button>
          </div>
        </a-form-item>
      </form-item>
    </div>

    <!-- 보상 임계치 리스트 -->
    <div v-if="form.rewardThresholds && form.rewardThresholds.length > 0" class="reward-thresholds-list">
      <div v-for="(threshold, index) in form.rewardThresholds" :key="index" class="reward-threshold-item">
        <div class="threshold-header">
          <div class="threshold-header-left">
            <a-button
              type="text"
              size="small"
              @click="toggleThreshold(index)"
              style="padding: 0 8px 0 0;"
            >
              <i
                :class="expandedThresholds[index] !== false ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'"
                style="font-size: 12px;"
              ></i>
            </a-button>
            <span class="threshold-title">
              보상 조건 {{ index + 1 }}
              <span v-if="threshold.rewardName" class="threshold-subtitle">
                - {{ threshold.rewardName }}
              </span>
            </span>
          </div>
          <a-button type="link" danger @click="removeRewardThreshold(index)">
            <i class="fa-solid fa-trash"></i> 삭제
          </a-button>
        </div>

        <div v-show="expandedThresholds[index] !== false">
          <div class="form-row">
            <form-item>
              <a-form-item
                label="보상 조건 타입"
              :name="['rewardThresholds', index, 'conditionType']"
              :rules="[{ required: true, message: '조건 타입을 선택해주세요' }]"
            >
              <a-select v-model:value="threshold.conditionType">
                <a-select-option value="share_count">공유 횟수</a-select-option>
                <a-select-option value="entry_count">응모 횟수</a-select-option>
                <a-select-option value="reservation_count">병원 예약 횟수</a-select-option>
                <a-select-option value="review_count">리뷰 작성 횟수</a-select-option>
                <a-select-option value="purchase_count">구매 인증 횟수</a-select-option>
                <a-select-option value="survey_complete">설문 완료</a-select-option>
                <a-select-option value="referral_count">추천인 수</a-select-option>
                <a-select-option value="custom">커스텀 조건</a-select-option>
              </a-select>
            </a-form-item>
          </form-item>

          <form-item>
            <a-form-item
              label="적용 대상"
              :name="['rewardThresholds', index, 'targetAudience']"
              tooltip="보상 조건이 적용되는 참여자 유형 (환자만, 친구만, 전체)"
            >
              <a-select v-model:value="threshold.targetAudience" placeholder="전체">
                <a-select-option value="all">전체</a-select-option>
                <a-select-option value="patient">환자만</a-select-option>
                <a-select-option value="friend">친구만</a-select-option>
              </a-select>
            </a-form-item>
          </form-item>
        </div>

        <div v-show="expandedThresholds[index] !== false">
          <div class="form-row">
          <form-item>
            <a-form-item
              label="임계치 값"
              :name="['rewardThresholds', index, 'thresholdValue']"
              :rules="[{ required: true, message: '임계치 값을 입력해주세요' }]"
            >
              <a-input-number
                v-model:value="threshold.thresholdValue"
                :min="1"
                :max="1000"
                style="width: 100%;"
                placeholder="예: 5"
              />
            </a-form-item>
          </form-item>
          </div>
        </div>

        <!-- 쿠폰 선택 UI -->
        <div class="coupon-selection-area">
          <div class="form-row">
            <form-item>
              <a-form-item
                label="쿠폰 공급사/계약"
                :name="['rewardThresholds', index, 'couponInfo', 'contractId']"
              >
                <a-select
                  :value="threshold.couponInfo?.contractId"
                  @change="(val) => handleContractChange(val, threshold, index)"
                  placeholder="공급사/계약 선택"
                  :loading="contractsLoading"
                  show-search
                  option-label-prop="label"
                  :filter-option="(input, option) => option.label?.toLowerCase().includes(input.toLowerCase())"
                >
                  <!-- 설정안함 고정 옵션 -->
                  <a-select-option
                    key="NONE"
                    value="NONE"
                    label="설정안함"
                  >
                    <div class="contract-option">
                      <span class="provider-tag none">없음</span>
                      <span class="contract-name">설정안함</span>
                    </div>
                  </a-select-option>
                  <a-select-option
                    v-for="contract in activeContracts"
                    :key="contract._id"
                    :value="contract._id"
                    :label="`[${contract.provider}] ${contract.displayName}`"
                  >
                    <div class="contract-option">
                      <span :class="['provider-tag', contract.provider?.toLowerCase()]">
                        {{ contract.provider }}
                      </span>
                      <span class="contract-name">{{ contract.displayName }}</span>
                    </div>
                  </a-select-option>
                </a-select>
              </a-form-item>
            </form-item>

            <form-item>
              <a-form-item
                label="쿠폰 상품"
                :name="['rewardThresholds', index, 'couponInfo', 'goodsId']"
                :rules="[{ required: threshold.couponInfo?.contractId && threshold.couponInfo?.contractId !== 'NONE', message: '쿠폰 상품을 선택해주세요' }]"
              >
                <a-select
                  :value="threshold.couponInfo?.goodsId"
                  @change="(val) => handleCouponProductSelect(val, threshold, index)"
                  placeholder="쿠폰 상품 선택"
                  :loading="productsLoading[index]"
                  :disabled="!threshold.couponInfo?.contractId || threshold.couponInfo?.contractId === 'NONE'"
                  allow-clear
                  show-search
                  option-label-prop="label"
                  :filter-option="(input, option) => option.label?.toLowerCase().includes(input.toLowerCase())"
                >
                  <a-select-option
                    v-for="product in (couponProducts[index] || [])"
                    :key="product.productCode"
                    :value="product.productCode"
                    :label="product.brandName ? `[${product.brandName}] ${product.productName}` : product.productName"
                  >
                    <div class="product-option">
                      <span v-if="product.brandName" class="brand-name">[{{ product.brandName }}]</span>
                      <span class="product-name">{{ product.productName }}</span>
                      <span v-if="product.salePrice" class="product-price">
                        {{ product.salePrice?.toLocaleString() }}원
                      </span>
                    </div>
                  </a-select-option>
                </a-select>
              </a-form-item>
            </form-item>
          </div>

          <!-- 선택된 쿠폰 정보 미리보기 -->
          <div v-if="threshold.couponInfo?.goodsId" class="coupon-preview">
            <div class="preview-header">
              <gift-outlined /> 선택된 쿠폰 정보
            </div>
            <div class="preview-content">
              <div class="preview-row">
                <span class="preview-label">상품명:</span>
                <span class="preview-value">{{ threshold.couponInfo.goodsName }}</span>
              </div>
              <div v-if="threshold.couponInfo.brand" class="preview-row">
                <span class="preview-label">브랜드:</span>
                <span class="preview-value">{{ threshold.couponInfo.brand }}</span>
              </div>
              <div class="preview-row">
                <span class="preview-label">공급사:</span>
                <span class="preview-value">
                  <span :class="['provider-tag', threshold.couponInfo.provider?.toLowerCase()]">
                    {{ threshold.couponInfo.provider }}
                  </span>
                  {{ threshold.couponInfo.contractDisplayName }}
                </span>
              </div>
              <div v-if="threshold.couponInfo.salePrice" class="preview-row">
                <span class="preview-label">판매가:</span>
                <span class="preview-value price">{{ threshold.couponInfo.salePrice?.toLocaleString() }}원</span>
                <span v-if="threshold.couponInfo.orgPrice && threshold.couponInfo.orgPrice !== threshold.couponInfo.salePrice" class="original-price">
                  {{ threshold.couponInfo.orgPrice?.toLocaleString() }}원
                </span>
              </div>
              <div v-if="threshold.couponInfo.expiry" class="preview-row">
                <span class="preview-label">유효기간:</span>
                <span class="preview-value">{{ threshold.couponInfo.expiry }}일</span>
              </div>
            </div>
          </div>
        </div>

        <div class="form-row">
          <form-item>
            <a-form-item
              label="보상 이름"
              :name="['rewardThresholds', index, 'rewardName']"
              :rules="[{ required: true, message: '보상 이름을 입력해주세요' }]"
            >
              <a-input
                v-model:value="threshold.rewardName"
                placeholder="예: 올리브영 1만원 쿠폰"
                :disabled="!!threshold.couponInfo?.goodsId"
              />
              <div class="description-text">
                쿠폰 상품 선택 시 자동으로 설정됩니다.
              </div>
            </a-form-item>
          </form-item>

          <form-item>
            <a-form-item
              label="수량 제한"
              tooltip="빈값 = 무제한, 현재 이벤트, 현재 보상조건의 최대 지급 가능 수량(재고개념)"
            >
              <a-input-number
                v-model:value="threshold.maxRewardCount"
                :min="1"
                style="width: 100%;"
                placeholder="무제한"
              />
            </a-form-item>
          </form-item>
        </div>

        <!-- 커스텀 조건 설명 -->
        <div v-if="threshold.conditionType === 'custom'" class="form-row">
          <form-item :fullWidth="true">
            <a-form-item
              label="커스텀 조건 설명"
              :name="['rewardThresholds', index, 'customConditionDescription']"
              :rules="[{ required: true, message: '커스텀 조건 설명을 입력해주세요' }]"
            >
              <a-input
                v-model:value="threshold.customConditionDescription"
                placeholder="예: 특정 제품 3개 이상 구매"
              />
            </a-form-item>
          </form-item>
        </div>

        <div class="form-row">
          <form-item :fullWidth="true">
            <a-form-item
              label="보상 설명"
              tooltip="사용자에게 표시될 설명 (선택사항)"
            >
              <a-textarea
                v-model:value="threshold.rewardDescription"
                placeholder="예: 5회 공유 완료 시 자동 지급됩니다"
                :rows="2"
              />
            </a-form-item>
          </form-item>
        </div>

        <div class="form-row">
          <form-item>
            <a-form-item
              label="알림톡 템플릿 코드"
              tooltip="미리 등록된 템플릿 코드 (선택사항)"
            >
              <a-input
                v-model:value="threshold.alimTalkTemplateCode"
                placeholder="예: REWARD_5_SHARE_COMPLETE"
              />
            </a-form-item>
          </form-item>

          <form-item>
            <a-form-item label="활성화 상태">
              <a-switch v-model:checked="threshold.isActive" />
              <span class="switch-label" style="margin-left: 8px;">
                {{ threshold.isActive !== false ? '활성화' : '비활성화' }}
              </span>
            </a-form-item>
          </form-item>
        </div>
        </div>
      </div>
    </div>

    <!-- 대량 참여자 생성 설정 -->
    <div class="form-row">
      <form-item :fullWidth="true">
        <a-form-item>
          <template #label>
            대량 참여자 생성
            <a-tooltip v-if="isEdit" title="대량 참여자 설정은 이벤트 생성 시에만 가능합니다">
              <i class="fa fa-question-circle" style="margin-left: 4px; color: #999;"></i>
            </a-tooltip>
          </template>
          <a-switch 
            v-model:checked="form.bulkAttendeeCreation.enabled" 
            :disabled="isEdit"
            @change="handleBulkCreationToggle"
          />
          <span style="margin-left: 12px;">
            {{ form.bulkAttendeeCreation.enabled ? '활성화' : '비활성화' }}
          </span>
          <span v-if="isEdit" style="margin-left: 12px; color: #999; font-size: 12px;">
            (수정 불가)
          </span>
          <div class="description-text">
            이벤트 생성 시 자동으로 대량의 참여자를 등록합니다.
          </div>
        </a-form-item>
      </form-item>
    </div>
    
    <!-- 대량 참여자 세부 설정 -->
    <div v-if="form.bulkAttendeeCreation.enabled && !isEdit" class="bulk-creation-settings">
      <div class="form-row">
        <form-item>
          <a-form-item 
            label="참여자 대상 유형"
            tooltip="대량 생성할 참여자의 대상을 선택합니다"
          >
            <a-select v-model:value="form.bulkAttendeeCreation.targetType">
              <a-select-option value="all">모든 고객 (블랙리스트 제외)</a-select-option>
              <a-select-option value="nft_holders">NFT/인증서 보유 고객</a-select-option>
              <a-select-option value="specific">특정 조건 지정</a-select-option>
            </a-select>
          </a-form-item>
        </form-item>
        
        <form-item>
          <a-form-item 
            label="배치 크기"
            tooltip="한 번에 처리할 데이터 건수 (100-10000)"
          >
            <a-input-number 
              v-model:value="form.bulkAttendeeCreation.options.batchSize"
              :min="100"
              :max="10000"
              :step="100"
              style="width: 100%;"
            />
          </a-form-item>
        </form-item>
      </div>
      
      <!-- 특정 조건 설정 -->
      <div v-if="form.bulkAttendeeCreation.targetType === 'specific'" class="specific-filters">
        <div class="form-row">
          <form-item :fullWidth="true">
            <a-form-item 
              label="특정 회사 ID"
              tooltip="참여 대상 회사 ID 목록 (콤마로 구분)"
            >
              <a-input 
                v-model:value="companyIdsText"
                placeholder="예: company123, company456"
                @blur="updateCompanyIds"
              />
            </a-form-item>
          </form-item>
        </div>
        
        <div class="form-row">
          <form-item>
            <a-form-item 
              label="가입일 시작"
              tooltip="이 날짜 이후 가입한 고객만 포함"
            >
              <a-date-picker 
                v-model:value="form.bulkAttendeeCreation.filters.createdAfter"
                show-time
                format="YYYY-MM-DD HH:mm:ss"
                style="width: 100%;"
              />
            </a-form-item>
          </form-item>
          
          <form-item>
            <a-form-item 
              label="가입일 종료"
              tooltip="이 날짜 이전 가입한 고객만 포함"
            >
              <a-date-picker 
                v-model:value="form.bulkAttendeeCreation.filters.createdBefore"
                show-time
                format="YYYY-MM-DD HH:mm:ss"
                style="width: 100%;"
              />
            </a-form-item>
          </form-item>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, getCurrentInstance, computed, onMounted, reactive } from 'vue';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import { QuestionCircleOutlined, WarningOutlined, GiftOutlined } from '@ant-design/icons-vue';
import FormItem from '../ui/FormItem.vue';
import FormRow from '../ui/FormRow.vue';
import eventsApis from '../../api';
import couponProviderApis from '../../../CouponProvider/api';
import axios from '@/axios';

export default {
  name: 'BasicInfoSection',
  components: {
    FormItem,
    FormRow,
    QuestionCircleOutlined,
    WarningOutlined,
    GiftOutlined,
  },
  props: {
    form: {
      type: Object,
      required: true
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  emits: ['search-modal-open', 'hospital-search-modal-open'],
  setup(props, { emit }) {
    // Vue 인스턴스 가져오기
    const instance = getCurrentInstance();
    const app = instance?.proxy;

    // Vuex store 및 API 초기화
    const store = useStore();
    const apis = eventsApis(axios, store);
    const couponApis = couponProviderApis(axios, store);

    // 입력 모드 상태 (search | direct)
    const inputMode = ref('search');

    // 직접 입력 값
    const directInputValue = ref('');

    // 보상 조건 펼침/접힘 상태 (기본값: 모두 펼침)
    const expandedThresholds = ref({});
    
    // 회사 ID 텍스트 (콤마 구분)
    const companyIdsText = ref('');

    // 이벤트 목록 (특정 이벤트 선택용)
    const eventList = ref([]);
    const eventListLoading = ref(false);

    // 주최자 목록 (특정 주최자 선택용 - companies 컬렉션에서 직접 조회)
    const organizerList = ref([]);
    const organizerListLoading = ref(false);

    // 쿠폰 관련 상태
    const activeContracts = ref([]); // 활성 계약 목록
    const contractsLoading = ref(false);
    const couponProducts = reactive({}); // threshold index별 상품 목록
    const productsLoading = reactive({}); // threshold index별 로딩 상태

    // 이벤트 목록 로드
    const loadEventList = async () => {
      eventListLoading.value = true;
      try {
        await apis.getEventsWithStatus(
          { status: 'all', limit: 500 },
          (response) => {
            const events = response.data?.items || response.data?.data || response.data || [];
            eventList.value = Array.isArray(events) ? events : [];
            console.log('이벤트 목록 로드 완료:', eventList.value.length, '개');
          },
          (error) => {
            console.error('이벤트 목록 로드 실패:', error);
            eventList.value = [];
          }
        );
      } finally {
        eventListLoading.value = false;
      }
    };

    // 주최자 목록 로드 (companies 컬렉션에서 직접 조회)
    const loadOrganizerList = async () => {
      organizerListLoading.value = true;
      try {
        await apis.getOrganizerList(
          { limit: 500 },
          (response) => {
            const docs = response.data?.docs || [];
            organizerList.value = docs.map(item => {
              // 주소 정보 조합
              const address = item.addrOne && item.addrTwo
                ? `${item.addrOne} ${item.addrTwo}${item.addrDetail ? ' ' + item.addrDetail : ''}`
                : (item.bizAddr || '');

              return {
                id: item._id,
                name: item.name,
                type: item.type || 'HOSPITAL',
                representative: item.representative || item.ceo || '',
                businessNumber: item.businessNumber || item.cNo || '',
                address: address
              };
            });
            // 이름순 정렬
            organizerList.value.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
            console.log('주최자 목록 로드 완료:', organizerList.value.length, '개');
          },
          (error) => {
            console.error('주최자 목록 로드 실패:', error);
            organizerList.value = [];
          }
        );
      } finally {
        organizerListLoading.value = false;
      }
    };

    // 이벤트 상태 텍스트 반환
    const getEventStatusText = (status) => {
      const statusMap = {
        'upcoming': '예정',
        'ongoing': '진행중',
        'completed': '종료',
        'cancelled': '취소',
      };
      return statusMap[status] || status || '알 수 없음';
    };

    // 이벤트 주최자 정보 포맷팅
    const getEventOrganizers = (event) => {
      if (!event?.organizers || event.organizers.length === 0) return '';
      const names = event.organizers
        .map(org => org.organizerName)
        .filter(Boolean);
      if (names.length === 0) return '';
      if (names.length <= 2) return names.join(', ');
      return `${names[0]} 외 ${names.length - 1}개`;
    };

    // 이벤트 검색 필터 옵션
    const filterEventOption = (input, option) => {
      const eventTitle = option.children?.[0]?.children || '';
      return eventTitle.toLowerCase().includes(input.toLowerCase());
    };

    // 주최자 타입 텍스트 반환
    const getOrganizerTypeText = (type) => {
      return type === 'MANUFACTURER' ? '제조사' : '병원';
    };

    // 주최자 검색 필터 옵션
    const filterOrganizerOption = (input, option) => {
      const organizerName = option.children?.[0]?.children || '';
      return organizerName.toLowerCase().includes(input.toLowerCase());
    };

    // 활성 쿠폰 계약 목록 로드
    const loadActiveContracts = async () => {
      contractsLoading.value = true;
      try {
        await couponApis.getActiveContracts(
          (response) => {
            activeContracts.value = response.data || [];
            console.log('활성 계약 목록 로드 완료:', activeContracts.value.length, '개');
          },
          (error) => {
            console.error('활성 계약 목록 로드 실패:', error);
            activeContracts.value = [];
          }
        );
      } finally {
        contractsLoading.value = false;
      }
    };

    // 특정 계약의 쿠폰 상품 목록 로드
    const loadCouponProducts = async (contractId, thresholdIndex) => {
      if (!contractId) {
        couponProducts[thresholdIndex] = [];
        return;
      }

      productsLoading[thresholdIndex] = true;
      try {
        await couponApis.getUnifiedProducts(
          { contractId },
          (response) => {
            const products = response.data?.products || [];
            couponProducts[thresholdIndex] = products;
            console.log(`계약 ${contractId}의 상품 ${products.length}개 로드됨`);
          },
          (error) => {
            console.error('쿠폰 상품 목록 로드 실패:', error);
            couponProducts[thresholdIndex] = [];
          }
        );
      } finally {
        productsLoading[thresholdIndex] = false;
      }
    };

    // 보상 타입 변경 핸들러
    const handleRewardTypeChange = (value, threshold, index) => {
      threshold.rewardType = value;

      if (value === 'coupon') {
        // 쿠폰 타입으로 변경 시 쿠폰 관련 필드 초기화 (DB 필드명 사용)
        threshold.couponInfo = {
          // 공급사 정보 (신규 필드)
          contractId: null,
          contractDisplayName: '',
          provider: '',
          // 기존 goodsInfo 호환 필드
          mid: 'certilife',
          goodsId: '',
          goodsName: '',
          brand: '',
          brandCategory: '',
          category1: '',
          category2: '',
          goodsImg: '',
          orgPrice: null,
          salePrice: null,
          offer: null,
          expiry: null,
          displayYN: '',
          limitDate: '',
          modelCode: ''
        };
        // 계약 목록이 없으면 로드
        if (activeContracts.value.length === 0) {
          loadActiveContracts();
        }
      } else {
        // 일반 타입으로 변경 시 쿠폰 정보 초기화
        threshold.couponInfo = null;
        delete couponProducts[index];
      }
    };

    // 쿠폰 계약 변경 핸들러
    const handleContractChange = async (contractId, threshold, index) => {
      // 선택 해제 또는 "설정안함" 선택 시
      if (!contractId || contractId === 'NONE') {
        threshold.couponInfo = {
          ...threshold.couponInfo,
          contractId: contractId || null,
          contractDisplayName: '',
          provider: '',
          // 기존 goodsInfo 호환 필드 초기화
          goodsId: '',
          goodsName: '',
          brand: '',
          brandCategory: '',
          category1: '',
          category2: '',
          goodsImg: '',
          orgPrice: null,
          salePrice: null,
          offer: null,
          expiry: null,
          displayYN: '',
          limitDate: '',
          modelCode: ''
        };
        couponProducts[index] = [];
        return;
      }

      // 선택된 계약 정보 찾기
      const contract = activeContracts.value.find(c => c._id === contractId);
      if (contract) {
        threshold.couponInfo = {
          ...threshold.couponInfo,
          contractId: contract._id,
          contractDisplayName: contract.displayName,
          provider: contract.provider,
          // 기존 goodsInfo 호환 필드 초기화
          goodsId: '',
          goodsName: '',
          brand: '',
          brandCategory: '',
          category1: '',
          category2: '',
          goodsImg: '',
          orgPrice: null,
          salePrice: null,
          offer: null,
          expiry: null,
          displayYN: '',
          limitDate: '',
          modelCode: ''
        };
      }

      // 해당 계약의 상품 목록 로드
      await loadCouponProducts(contractId, index);
    };

    // 쿠폰 상품 선택 핸들러
    // API 응답 필드(productCode, productName 등)를 DB 필드(goodsId, goodsName 등)로 매핑
    const handleCouponProductSelect = (productCode, threshold, index) => {
      if (!productCode) {
        threshold.couponInfo = {
          ...threshold.couponInfo,
          // DB 필드명 사용 (기존 goodsInfo 구조와 호환)
          goodsId: '',
          goodsName: '',
          brand: '',
          brandCategory: '',
          category1: '',
          category2: '',
          orgPrice: null,
          salePrice: null,
          expiry: null,
          goodsImg: '',
          offer: null,
          displayYN: '',
          limitDate: '',
          modelCode: '',
          mid: 'certilife'
        };
        threshold.rewardName = '';
        return;
      }

      // 선택된 상품 정보 찾기
      const products = couponProducts[index] || [];
      const product = products.find(p => p.productCode === productCode);

      if (product) {
        // API 필드명 -> DB 필드명 매핑
        threshold.couponInfo = {
          ...threshold.couponInfo,
          // 공급사 정보 (신규 필드)
          // contractId, contractDisplayName, provider는 handleContractChange에서 이미 설정됨
          // DB 필드명으로 저장 (기존 goodsInfo 구조와 호환)
          mid: 'certilife',
          goodsId: product.productCode,      // productCode -> goodsId
          goodsName: product.productName,    // productName -> goodsName
          brand: product.brandName,          // brandName -> brand
          brandCategory: product.brandCategory || '',
          category1: product.category?.split(' > ')[0] || '',
          category2: product.category?.split(' > ')[1] || '',
          goodsImg: product.imageUrl,        // imageUrl -> goodsImg
          orgPrice: product.originalPrice,   // originalPrice -> orgPrice
          salePrice: product.salePrice,
          offer: product.originalPrice && product.salePrice
            ? Math.round((1 - product.salePrice / product.originalPrice) * 100)
            : null,
          expiry: product.validDays,         // validDays -> expiry
          displayYN: product.isOnSale ? 'Y' : 'N',
          limitDate: '',
          modelCode: 'certilife001'
        };

        // 보상 이름 자동 설정
        const brandPrefix = product.brandName ? `[${product.brandName}] ` : '';
        threshold.rewardName = `${brandPrefix}${product.productName}`;

        app?.$message.success('쿠폰 상품이 선택되었습니다.');
      }
    };

    // 컴포넌트 마운트 시 이벤트 목록 및 주최자 목록 로드
    onMounted(() => {
      loadEventList();
      loadOrganizerList();
      loadActiveContracts(); // 쿠폰 계약 목록도 미리 로드
    });

    // 병원 검색 모달 열기
    const handleHospitalSearchModalOpen = () => {
      emit('hospital-search-modal-open');
    };

    // 시나리오 유형 커스텀 모드 여부
    const isCustomScenarioType = computed(() => props.form.scenarioType === 'custom');

    // 과거 이벤트 참여자 차단 범위가 하나라도 선택되었는지 확인
    const hasAnyBlockScope = computed(() => {
      const config = props.form.blockPreviousParticipants;
      if (!config || !config.enabled) return false;
      return config.checkAll ||
             config.checkSameOrganizer ||
             config.checkSpecificOrganizers ||
             config.checkSpecificEvents;
    });

    // 주최자 검색 모달 열기
    const handleSearchModalOpen = () => {
      emit('search-modal-open');
    };
    
    // 직접 입력한 주최자 추가
    const addDirectOrganizer = () => {
      const value = directInputValue.value?.trim();
      
      if (!value) {
        app?.$message.warning('주최자명을 입력해주세요.');
        return;
      }
      
      // 중복 체크
      const isDuplicate = props.form.organizers.some(org => 
        org.organizerName === value
      );
      
      if (isDuplicate) {
        app?.$message.warning('이미 추가된 주최자입니다.');
        return;
      }
      
      // 주최자 추가
      if (!props.form.organizers) {
        props.form.organizers = [];
      }
      
      props.form.organizers.push({
        organizerId: `custom-${Date.now()}`,
        organizerName: value,
        representative: '',
        organizerCno: '',
        directInput: true
      });
      
      // 입력 필드 초기화
      directInputValue.value = '';
      app?.$message.success('주최자가 추가되었습니다.');
    };
    
    // 주최자 제거
    const removeOrganizer = (index) => {
      props.form.organizers.splice(index, 1);
      app?.$message.success('주최자가 제거되었습니다.');
    };
    
    // 시나리오 타입 변경 처리
    const handleScenarioTypeChange = (e) => {
      const value = e.target.value;
      props.form.scenarioType = value;
      
      if (value === 'custom') {
        // 직접 입력 모드로 전환
        props.form.customScenarioType = '';
      } else {
        // 선택한 시나리오 유형으로 설정
        props.form.customScenarioType = '';
      }
    };
    
    // 커스텀 시나리오 타입 취소
    const cancelCustomScenarioType = () => {
      props.form.scenarioType = 'review'; // 기본값으로 복귀
      props.form.customScenarioType = '';
    };
    
    // 시작일 비활성화 함수 - 제약 없음
    const disabledStartDate = (current) => {
      return false; // 모든 날짜 선택 가능
    };
    
    // 종료일 비활성화 함수 - 시작일 이전 날짜는 선택 불가
    const disabledEndDate = (current) => {
      if (!current) return false;
      
      // 시작일이 선택되었으면, 시작일 이전 날짜는 비활성화
      if (props.form.startDate) {
        return current < dayjs(props.form.startDate).startOf('day');
      }
      
      return false;
    };
    
    // 대량 참여자 생성 토글 핸들러
    const handleBulkCreationToggle = (checked) => {
      if (!checked) {
        // 비활성화 시 관련 설정 초기화
        props.form.bulkAttendeeCreation.targetType = 'all';
        props.form.bulkAttendeeCreation.filters = {
          companyIds: [],
          excludeCustomerIds: [],
          createdAfter: null,
          createdBefore: null
        };
      }
    };
    
    // 회사 ID 업데이트
    const updateCompanyIds = () => {
      const ids = companyIdsText.value
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);
      props.form.bulkAttendeeCreation.filters.companyIds = ids;
    };
    
    // bulkAttendeeCreation.filters.companyIds 변경 감지
    watch(() => props.form.bulkAttendeeCreation?.filters?.companyIds, (newVal) => {
      if (newVal && Array.isArray(newVal)) {
        companyIdsText.value = newVal.join(', ');
      }
    }, { immediate: true });

    // 보상 임계치 추가
    const addRewardThreshold = () => {
      if (!props.form.rewardThresholds) {
        props.form.rewardThresholds = [];
      }

      props.form.rewardThresholds.push({
        conditionType: '',
        targetAudience: 'all',  // 기본값: 전체 적용
        thresholdValue: 1,
        rewardType: 'coupon',  // 기본값: 쿠폰 보상 (general/coupon)
        rewardName: '',
        rewardDescription: '',
        maxRewardCount: null,
        isActive: true,
        alimTalkTemplateCode: '',
        customConditionDescription: '',
        // 쿠폰 타입이 기본값이므로 couponInfo 초기화 (DB 필드명 사용)
        couponInfo: {
          // 공급사 정보 (신규 필드)
          contractId: null,
          contractDisplayName: '',
          provider: '',
          // 기존 goodsInfo 호환 필드
          mid: 'certilife',
          goodsId: '',
          goodsName: '',
          brand: '',
          brandCategory: '',
          category1: '',
          category2: '',
          goodsImg: '',
          orgPrice: null,
          salePrice: null,
          offer: null,
          expiry: null,
          displayYN: '',
          limitDate: '',
          modelCode: ''
        }
      });

      app?.$message.success('보상 조건이 추가되었습니다.');
    };

    // 보상 임계치 제거
    const removeRewardThreshold = (index) => {
      props.form.rewardThresholds.splice(index, 1);
      // 펼침 상태도 함께 제거
      delete expandedThresholds.value[index];
      app?.$message.success('보상 조건이 제거되었습니다.');
    };

    // 보상 임계치 펼침/접힘 토글
    const toggleThreshold = (index) => {
      // undefined 또는 true인 경우 false로, false인 경우 true로
      expandedThresholds.value[index] = expandedThresholds.value[index] === false ? true : false;
    };

    // 알림톡 템플릿 미리보기 관련
    const templateLoading = ref(false);
    const templatePreviewVisible = ref(false);
    const templatePreviewData = ref(null);

    // 템플릿 미리보기
    const previewTemplate = async () => {
      const templateCode = props.form.alimtalkTemplateCode?.trim();
      if (!templateCode) {
        app?.$message.warning('템플릿 코드를 입력해주세요.');
        return;
      }

      templateLoading.value = true;
      templatePreviewData.value = null;

      try {
        const response = await app?.$apiInstance.get(`/bizmsg/resell/templates/${templateCode}`);

        if (response?.data?.success && response?.data?.data) {
          const template = response.data.data;
          templatePreviewData.value = {
            code: template.code || templateCode,
            name: template.name || '알 수 없음',
            content: template.content || template.msgContent || '내용 없음',
            buttons: template.buttons || template.btns || []
          };
          templatePreviewVisible.value = true;
        } else {
          app?.$message.error('템플릿 정보를 조회할 수 없습니다.');
        }
      } catch (error) {
        console.error('템플릿 조회 실패:', error);
        app?.$message.error(error?.response?.data?.message || '템플릿 조회에 실패했습니다.');
      } finally {
        templateLoading.value = false;
      }
    };

    // 이벤트 적용 범위 변경 핸들러
    const handleTargetScopeChange = (e) => {
      const value = e.target.value;
      props.form.targetScope = value;

      // SPECIFIC에서 ALL로 변경 시 targetHospitals 초기화
      if (value === 'ALL') {
        props.form.targetHospitals = [];
      }
    };

    // 적용 대상 병원 제거
    const removeTargetHospital = (index) => {
      props.form.targetHospitals.splice(index, 1);
      app?.$message.success('병원이 제거되었습니다.');
    };

    return {
      inputMode,
      directInputValue,
      isCustomScenarioType,
      handleSearchModalOpen,
      handleScenarioTypeChange,
      cancelCustomScenarioType,
      addDirectOrganizer,
      removeOrganizer,
      disabledStartDate,
      disabledEndDate,
      handleBulkCreationToggle,
      updateCompanyIds,
      companyIdsText,
      addRewardThreshold,
      removeRewardThreshold,
      toggleThreshold,
      expandedThresholds,
      // 템플릿 미리보기
      templateLoading,
      templatePreviewVisible,
      templatePreviewData,
      previewTemplate,
      // 이벤트 적용 범위
      handleTargetScopeChange,
      handleHospitalSearchModalOpen,
      removeTargetHospital,
      // 과거 이벤트 참여자 차단
      hasAnyBlockScope,
      // 특정 주최자/이벤트 선택용
      eventList,
      eventListLoading,
      getEventStatusText,
      getEventOrganizers,
      filterEventOption,
      organizerList,
      organizerListLoading,
      filterOrganizerOption,
      getOrganizerTypeText,
      // 쿠폰 관련
      activeContracts,
      contractsLoading,
      couponProducts,
      productsLoading,
      handleContractChange,
      handleCouponProductSelect,
    };
  }
};
</script>

<style scoped lang="scss">
.form-section {
  margin-bottom: 28px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eaeaea;
}

.section-description {
  color: #666;
  font-size: 13px;
  margin: 0 0 16px 0;
  line-height: 1.5;
}

// 주최자 관련 새로운 스타일
.organizers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  min-height: 44px;
}

.organizer-tag {
  font-size: 14px;
  padding: 4px 12px;
  height: auto;
  min-height: 32px;
  line-height: 24px;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  .organizer-type-badge {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    flex-shrink: 0;

    &.hospital {
      background-color: #e6f7ff;
      color: #1890ff;
      border: 1px solid #91d5ff;
    }

    &.manufacturer {
      background-color: #f6ffed;
      color: #52c41a;
      border: 1px solid #b7eb8f;
    }
  }

  .organizer-name {
    font-weight: 500;
    color: #333;
  }

  .sub-info {
    font-size: 11px;
    color: #888;
  }
}

.organizer-input-area {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.input-mode-selector {
  margin-bottom: 8px;
}

.search-mode, .direct-mode {
  padding: 8px 0;
}

.direct-mode {
  .custom-input {
    width: 300px;
    padding: 4px 11px;
    font-size: 14px;
    line-height: 1.5715;
    background-color: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 2px;
    transition: all 0.3s;
    
    &:hover {
      border-color: #40a9ff;
    }
    
    &:focus {
      border-color: #40a9ff;
      outline: 0;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }
    
    &::placeholder {
      color: #bfbfbf;
    }
  }
}

.description-text {
  font-size: 11px !important;
  color: #8c8c8c;
  margin: 4px 0 0 0 !important;
  line-height: 1.2 !important;
}

/* 수신 페이지 모드 라디오 힌트 텍스트 (REQ-R3-C1) */
.radio-hint {
  font-size: 11px;
  color: #8c8c8c;
  margin-left: 4px;
}

/* ongoing 상태 즉시 반영 경고 안내 (AC5-S4) */
.description-text--warning {
  color: #d46b08 !important;
}

.event-type-selector {
  margin-bottom: 8px;
}

.custom-event-type {
  padding: 8px 0;
}

// 시나리오 관련 스타일
.scenario-preview {
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 12px;
  margin-top: 8px;
  background-color: #f9f9f9;
}

.scenario-card {
  h3 {
    margin-top: 0;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  p {
    font-size: 12px;
    color: #666;
    margin-bottom: 8px;
  }
}

.scenario-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  
  .scenario-tag {
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    background-color: #f5f5f5;
    color: #999;
    cursor: help;
    
    &.active {
      background-color: #1890ff;
      color: white;
      font-weight: 500;
      box-shadow: 0 1px 2px rgba(24, 144, 255, 0.2);
    }
    
    &.inactive {
      background-color: #f0f0f0;
      color: #bbb;
      text-decoration: line-through;
      opacity: 0.7;
    }
  }
}

// 보상 임계치 설정 스타일
.reward-threshold-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.reward-thresholds-list {
  margin-top: 16px;
}

.reward-threshold-item {
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f0f8ff;
  border: 1px solid #b3d9ff;
  border-radius: 4px;
}

.threshold-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #d9d9d9;
}

.threshold-header-left {
  display: flex;
  align-items: center;
}

.threshold-title {
  font-weight: 600;
  font-size: 14px;
}

.threshold-subtitle {
  font-weight: 400;
  color: #666;
  margin-left: 4px;
}

// 대량 참여자 생성 설정 스타일
.bulk-creation-settings {
  margin-top: 16px;
  padding: 16px;
  background-color: #f9f9f9;
  border: 1px solid #e8e8e8;
  border-radius: 4px;

  .specific-filters {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e0e0e0;
  }
}

// 알림톡 템플릿 입력 스타일
.alimtalk-template-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

// 환경변수 설정 키 입력 스타일
.env-config-key-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

// 템플릿 미리보기 모달 스타일
.template-preview {
  .preview-item {
    margin-bottom: 16px;

    label {
      display: block;
      font-weight: 600;
      color: #333;
      margin-bottom: 4px;
    }

    span {
      color: #666;
    }

    .message-content {
      background-color: #f5f5f5;
      padding: 12px;
      border-radius: 4px;
      white-space: pre-wrap;
      font-size: 13px;
      line-height: 1.5;
      max-height: 300px;
      overflow-y: auto;
    }

    .buttons-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .button-item {
        padding: 4px 12px;
        background-color: #e6f7ff;
        border: 1px solid #91d5ff;
        border-radius: 4px;
        font-size: 12px;
        color: #1890ff;
      }
    }
  }
}

.template-loading {
  text-align: center;
  padding: 24px;
  color: #666;
}

.template-error {
  text-align: center;
  padding: 24px;
  color: #ff4d4f;
}

// 이벤트 적용 범위 관련 스타일
.target-hospitals-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f0f8ff;
  border: 1px solid #b3d9ff;
  border-radius: 4px;
  min-height: 44px;
}

.hospital-tag {
  font-size: 14px;
  padding: 4px 12px;
  height: 32px;
  line-height: 24px;
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;

  .hospital-id-hint {
    font-size: 11px;
    color: #999;
    margin-left: 4px;
    font-family: monospace;
  }
}

.hospital-input-area {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

// 과거 이벤트 참여자 차단 설정 스타일
.block-config-container {
  width: 100%;
}

.block-config-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.block-config-options {
  margin-top: 16px;
  padding: 16px;
  background-color: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}

.block-scope-title {
  font-weight: 500;
  font-size: 13px;
  color: #333;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8e8e8;
}

.block-scope-item {
  margin-bottom: 16px;
  padding-left: 4px;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.block-scope-desc {
  font-size: 12px;
  color: #8c8c8c;
  margin: 4px 0 0 24px;
  line-height: 1.4;
}

.block-scope-input {
  margin: 8px 0 0 24px;

  .ant-input {
    font-family: monospace;
    font-size: 12px;
  }
}

.input-count {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
  text-align: right;
}

.block-scope-message,
.coupon-depleted-message {
  margin-top: 12px;
  padding: 12px;
  background-color: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;

  .message-label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #595959;
    margin-bottom: 8px;
  }

  .ant-input {
    font-size: 13px;
  }
}

.block-scope-summary {
  margin-top: 16px;
  padding: 12px;
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  font-size: 13px;
}

.scope-tag {
  display: inline-block;
  margin: 2px 4px;
  padding: 2px 8px;
  background-color: #1890ff;
  color: white;
  border-radius: 4px;
  font-size: 12px;
}

.block-scope-warning {
  margin-top: 16px;
  padding: 12px;
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  font-size: 13px;
  color: #ad6800;
}

// 세그먼트 필터 스타일
.block-segment-filter {
  margin-top: 8px;

  .segment-filter-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;

    .ant-radio-wrapper {
      margin-right: 0;
      padding: 8px 12px;
      background-color: white;
      border: 1px solid #e8e8e8;
      border-radius: 6px;
      transition: all 0.2s;

      &:hover {
        border-color: #1890ff;
      }

      &.ant-radio-wrapper-checked {
        border-color: #1890ff;
        background-color: #e6f7ff;
      }
    }
  }

  .segment-option {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .segment-label {
      font-weight: 500;
      font-size: 13px;
      color: #333;
    }

    .segment-desc {
      font-size: 11px;
      color: #8c8c8c;
    }
  }
}

.scope-tag.segment {
  background-color: #722ed1;
}

// 환자/친구 최대 쿠폰 수량 설정 스타일
.coupon-limits-container {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 16px;
  background-color: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
}

.coupon-limit-item {
  display: flex;
  align-items: center;
  gap: 12px;

  .limit-header {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 140px;

    .limit-badge {
      display: inline-block;
      padding: 2px 10px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;

      &.patient {
        background-color: #e6f7ff;
        color: #1890ff;
        border: 1px solid #91d5ff;
      }

      &.friend {
        background-color: #f6ffed;
        color: #52c41a;
        border: 1px solid #b7eb8f;
      }
    }

    .limit-label {
      font-size: 13px;
      color: #595959;
    }
  }

  .limit-unit {
    font-size: 13px;
    color: #8c8c8c;
  }
}

// 쿠폰 선택 영역 스타일
.coupon-selection-area {
  margin-bottom: 16px;
  padding: 16px;
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
}

.coupon-preview {
  margin-top: 16px;
  padding: 12px;
  background-color: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;

  .preview-header {
    font-weight: 600;
    font-size: 13px;
    color: #52c41a;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e8e8e8;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .preview-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .preview-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;

    .preview-label {
      color: #8c8c8c;
      min-width: 60px;
    }

    .preview-value {
      color: #333;
      font-weight: 500;

      &.price {
        color: #ff4d4f;
        font-weight: 600;
      }
    }

    .original-price {
      color: #999;
      text-decoration: line-through;
      font-size: 12px;
    }
  }
}

.provider-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;

  &.giftiel {
    background-color: #f6ffed;
    color: #52c41a;
    border: 1px solid #b7eb8f;
  }

  &.sendb {
    background-color: #e6f7ff;
    color: #1890ff;
    border: 1px solid #91d5ff;
  }

  &.none {
    background-color: #f5f5f5;
    color: #999;
    border: 1px solid #d9d9d9;
  }
}
</style>

<style lang="scss">
// 주최자 옵션 스타일 (드롭다운은 portal로 렌더링되므로 unscoped)
.organizer-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;

  .organizer-type-tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    flex-shrink: 0;

    &.hospital {
      background-color: #e6f7ff;
      color: #1890ff;
      border: 1px solid #91d5ff;
    }

    &.manufacturer {
      background-color: #f6ffed;
      color: #52c41a;
      border: 1px solid #b7eb8f;
    }
  }

  .organizer-name {
    font-weight: 500;
    color: #333;
  }

  .organizer-details {
    color: #999;
    font-size: 12px;
  }
}

// 이벤트 옵션 스타일 (드롭다운은 portal로 렌더링되므로 unscoped)
.event-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;

  .event-status-tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    flex-shrink: 0;

    &.ongoing {
      background-color: #f6ffed;
      color: #52c41a;
      border: 1px solid #b7eb8f;
    }

    &.completed {
      background-color: #f5f5f5;
      color: #8c8c8c;
      border: 1px solid #d9d9d9;
    }

    &.preparing, &.upcoming {
      background-color: #e6f7ff;
      color: #1890ff;
      border: 1px solid #91d5ff;
    }

    &.cancelled {
      background-color: #fff1f0;
      color: #ff4d4f;
      border: 1px solid #ffa39e;
    }
  }

  .event-title {
    font-weight: 500;
    color: #333;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .event-organizers {
    color: #999;
    font-size: 12px;
    flex-shrink: 0;
  }
}

// 쿠폰 계약 옵션 스타일 (드롭다운은 portal로 렌더링되므로 unscoped)
.contract-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;

  .provider-tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    flex-shrink: 0;

    &.giftiel {
      background-color: #f6ffed;
      color: #52c41a;
      border: 1px solid #b7eb8f;
    }

    &.sendb {
      background-color: #e6f7ff;
      color: #1890ff;
      border: 1px solid #91d5ff;
    }

    &.none {
      background-color: #f5f5f5;
      color: #999;
      border: 1px solid #d9d9d9;
    }
  }

  .contract-name {
    font-weight: 500;
    color: #333;
  }
}

// 쿠폰 상품 옵션 스타일 (드롭다운은 portal로 렌더링되므로 unscoped)
.product-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;

  .brand-name {
    color: #1890ff;
    font-weight: 500;
    font-size: 12px;
  }

  .product-name {
    color: #333;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .product-price {
    color: #ff4d4f;
    font-weight: 500;
    font-size: 12px;
    flex-shrink: 0;
  }
}

// 차단 로직 다이어그램 Popover 스타일 (portal로 렌더링되므로 unscoped)
.block-logic-popover {
  .ant-popover-inner {
    border-radius: 8px;
  }
}

.block-logic-diagram {
  width: 280px;

  .diagram-title {
    font-weight: 600;
    font-size: 14px;
    color: #333;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #1890ff;
  }

  .diagram-flow {
    display: flex;
    flex-direction: column;
    align-items: center;

    .flow-start {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 8px 20px;
      border-radius: 20px;
      font-weight: 500;
      font-size: 13px;
    }

    .flow-arrow {
      color: #999;
      font-size: 16px;
      margin: 8px 0;
    }

    .flow-checks {
      width: 100%;
      background: #f7f7f7;
      border-radius: 8px;
      padding: 12px;

      .check-item {
        background: white;
        border: 1px solid #e8e8e8;
        border-radius: 6px;
        padding: 8px 12px;

        .check-box {
          font-weight: 500;
          font-size: 12px;
          color: #333;
        }

        .check-desc {
          font-size: 11px;
          color: #999;
          margin-top: 2px;
        }
      }

      .flow-or {
        text-align: center;
        color: #ff7a45;
        font-weight: 600;
        font-size: 11px;
        margin: 6px 0;
      }
    }

    .flow-results {
      display: flex;
      gap: 12px;
      margin-top: 4px;

      .result-block {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 10px 12px;
        border-radius: 8px;
        flex: 1;

        &.blocked {
          background: #fff1f0;
          border: 1px solid #ffa39e;
        }

        &.passed {
          background: #f6ffed;
          border: 1px solid #b7eb8f;
        }

        .result-icon {
          font-size: 18px;
          margin-bottom: 4px;
        }

        .result-text {
          font-size: 11px;
          color: #333;
          text-align: center;
          line-height: 1.3;
        }
      }
    }
  }

  .diagram-note {
    margin-top: 12px;
    padding: 8px 10px;
    background: #fffbe6;
    border: 1px solid #ffe58f;
    border-radius: 4px;
    font-size: 11px;
    color: #ad6800;
    display: flex;
    align-items: center;
  }
}
</style> 